import React, { useState } from "react";
import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { OrderVersionDto } from "../../../interfaces/OrderDetail";
import { OrderDetailsDraft, StateCode } from "./hooks/useOrderDetailsEdit";
import {
  hasFobChanges,
  hasPoolOwnerChanges,
  hasShipToChanges,
  hasShipViaChanges,
  hasValue,
} from "./quoteVersionUtils";
import { PendingChangeNote } from "./PendingChangeNote";
import ShipToAddressModal, { ShipToAddressValues } from "./ShipToAddressModal";
import PoolOwnerModal, { PoolOwnerValues } from "./PoolOwnerModal";

const SHIP_VIA_OPTIONS = [
  "PICK UP",
  "PREPAID",
  "PPY+ADD",
  "BEST WAY",
  "BEST WAY(C)",
  "BEST WAY(S)",
  "BEST WAY(R)",
  "BEST WAY(CA)",
  "BEST WAY(MH)",
  "BEST WAY(X)",
];

interface AddressColumnsProps {
  version: OrderVersionDto;
  editing: boolean;
  saving: boolean;
  draft: OrderDetailsDraft;
  onDraftChange: (patch: Partial<OrderDetailsDraft>) => void;
  usStateOptions: StateCode[];
}

export const AddressColumns: React.FC<AddressColumnsProps> = ({
  version,
  editing,
  saving,
  draft,
  onDraftChange,
  usStateOptions,
}) => {
  const [shipToModalOpen, setShipToModalOpen] = useState(false);
  const [poolOwnerModalOpen, setPoolOwnerModalOpen] = useState(false);

  const dealerCityStateZip = [
    version.dealerCity,
    [version.dealerState, version.dealerZip].filter(hasValue).join(" "),
  ]
    .filter(hasValue)
    .join(", ");

  const shippingCityStateZip = [
    version.shippingCity,
    [version.shippingState, version.shippingZip].filter(hasValue).join(" "),
  ]
    .filter(hasValue)
    .join(", ");

  const poolOwnerName = [version.poolOwnerFirstName, version.poolOwnerLastName]
    .filter(hasValue)
    .join(" ");

  const poolOwnerCityStateZip = [
    version.poolOwnerCity,
    [version.poolOwnerState, version.poolOwnerZip].filter(hasValue).join(" "),
  ]
    .filter(hasValue)
    .join(", ");

  // Build Ship Via options — include legacy value if not in fixed list
  const shipViaOptions = [...SHIP_VIA_OPTIONS];
  if (hasValue(version.shipVia) && !shipViaOptions.includes(version.shipVia!)) {
    shipViaOptions.unshift(version.shipVia!);
  }

  const displayFobSection =
    editing ||
    hasValue(version.fob) ||
    hasValue(version.shipVia) ||
    hasValue(version.phone) ||
    hasValue(version.fax) ||
    hasValue(version.email);

  return (
    <>
      {/* ── Bill To / Ship To / Pool Owner ── */}
      <Row gutter={16} style={{ marginBottom: 12 }}>
        {/* Bill To (read-only) */}
        <Col xs={24} sm={8}>
          <Typography.Text strong>
            Bill To{" "}
            {hasValue(version.dealerAccountNumber)
              ? `Acct: ${version.dealerAccountNumber}`
              : null}
          </Typography.Text>
          <div style={{ marginTop: 4 }}>
            {hasValue(version.dealerName) && <div>{version.dealerName}</div>}
            {hasValue(version.dealerAddress) && (
              <div>{version.dealerAddress}</div>
            )}
            {hasValue(dealerCityStateZip) && <div>{dealerCityStateZip}</div>}
          </div>
        </Col>

        {/* Ship To */}
        <Col xs={24} sm={8}>
          <Typography.Text strong>Ship To </Typography.Text>
          {editing && (
            <Button
              icon={<EditOutlined />}
              disabled={saving}
              onClick={() => setShipToModalOpen(true)}
            />
          )}
          {editing ? (
            <div style={{ marginTop: 4 }}>
              {hasValue(draft.shippingName) && <div>{draft.shippingName}</div>}
              {hasValue(draft.shippingAddress) && (
                <div>{draft.shippingAddress}</div>
              )}
              {hasValue(draft.shippingCity) && (
                <div>
                  {[
                    draft.shippingCity,
                    [draft.shippingState, draft.shippingZip]
                      .filter(hasValue)
                      .join(" "),
                  ]
                    .filter(hasValue)
                    .join(", ")}
                </div>
              )}
              <ShipToAddressModal
                open={shipToModalOpen}
                onClose={() => setShipToModalOpen(false)}
                onSave={(values: ShipToAddressValues) => {
                  // Stage the new values into the shared draft — the actual
                  // PATCH fires when the user clicks Save in QuoteVersionDetail.
                  onDraftChange({
                    shippingName: values.shippingName,
                    shippingAddress: values.shippingAddress,
                    shippingCity: values.shippingCity,
                    shippingState: values.shippingState,
                    shippingZip: values.shippingZip,
                  });
                }}
                initialValues={{
                  shippingName: draft.shippingName,
                  shippingAddress: draft.shippingAddress,
                  shippingCity: draft.shippingCity,
                  shippingState: draft.shippingState,
                  shippingZip: draft.shippingZip,
                }}
                poolOwner={{
                  name: poolOwnerName,
                  address: version.poolOwnerAddress ?? "",
                  city: version.poolOwnerCity ?? "",
                  state: version.poolOwnerState ?? "",
                  zip: version.poolOwnerZip ?? "",
                }}
                usStateOptions={usStateOptions}
              />
              {hasShipToChanges(draft, version) && <PendingChangeNote />}
            </div>
          ) : (
            <div style={{ marginTop: 4 }}>
              {hasValue(version.shippingName) && (
                <div>{version.shippingName}</div>
              )}
              {hasValue(version.shippingAddress) && (
                <div>{version.shippingAddress}</div>
              )}
              {hasValue(version.shippingAddress2) && (
                <div>{version.shippingAddress2}</div>
              )}
              {hasValue(version.shippingAddress3) && (
                <div>{version.shippingAddress3}</div>
              )}
              {hasValue(shippingCityStateZip) && (
                <div>{shippingCityStateZip}</div>
              )}
            </div>
          )}
        </Col>

        {/* Pool Owner */}
        <Col xs={24} sm={8}>
          <Typography.Text strong>Pool Owner </Typography.Text>
          {editing && (
            <Button
              icon={<EditOutlined />}
              disabled={saving}
              onClick={() => !saving && setPoolOwnerModalOpen(true)}
            />
          )}
          <div style={{ marginTop: 4 }}>
            {hasValue(editing ? draft.poolOwnerFirstName || draft.poolOwnerLastName : poolOwnerName) && (
              <div>{editing ? [draft.poolOwnerFirstName, draft.poolOwnerLastName].filter(Boolean).join(" ") : poolOwnerName}</div>
            )}
            {hasValue(editing ? draft.poolOwnerAddress : version.poolOwnerAddress) && (
              <div>{editing ? draft.poolOwnerAddress : version.poolOwnerAddress}</div>
            )}
            {hasValue(editing ? draft.poolOwnerCity : poolOwnerCityStateZip) && (
              <div>
                {editing
                  ? [draft.poolOwnerCity, [draft.poolOwnerState, draft.poolOwnerZip].filter(hasValue).join(" ")].filter(hasValue).join(", ")
                  : poolOwnerCityStateZip}
              </div>
            )}
            {editing && hasPoolOwnerChanges(draft, version) && (
              <PendingChangeNote />
            )}
          </div>
          <PoolOwnerModal
            open={poolOwnerModalOpen}
            onClose={() => setPoolOwnerModalOpen(false)}
            onSave={(values: PoolOwnerValues) => onDraftChange(values)}
            initialValues={{
              poolOwnerFirstName: draft.poolOwnerFirstName,
              poolOwnerLastName: draft.poolOwnerLastName,
              poolOwnerAddress: draft.poolOwnerAddress,
              poolOwnerCity: draft.poolOwnerCity,
              poolOwnerState: draft.poolOwnerState,
              poolOwnerZip: draft.poolOwnerZip,
            }}
            usStateOptions={usStateOptions}
          />
        </Col>
      </Row>

      {/* ── FOB / Ship Via / Contact ── */}
      {displayFobSection && (
        <Descriptions
          size="small"
          column={4}
          style={{ marginBottom: 12, marginTop: 12 }}
          labelStyle={{ fontWeight: 600 }}
        >
          {/* Per John on 8/5/26 - Make FOB non-editable for now */}
          {hasValue(version.fob) && (
            <Descriptions.Item label={"FOB"}>{version.fob}</Descriptions.Item>
          )}
          {/* {hasValue(version.fob) && editing && (
            <Form.Item
              label="FOB"
              required
              validateStatus={!draft.fob.trim() ? "error" : ""}
              help={!draft.fob.trim() ? "Required" : undefined}
              style={{ margin: 0 }}
            >
              <Space direction="vertical" size="small">
                <Input
                  value={draft.fob}
                  onChange={(e) => onDraftChange({ fob: e.target.value })}
                  disabled={saving}
                />
                {hasFobChanges(draft, version) && <PendingChangeNote />}
              </Space>
            </Form.Item>
          )} */}

          {hasValue(version.shipVia) && (
            <Descriptions.Item label="Ship Via">
              {version.shipVia}
            </Descriptions.Item>
          )}
          {/* Per John on 8/5/26 - Make Ship VIA non-editable for now */}
          {/* {hasValue(version.shipVia) && editing && (
            <Form.Item
              label="Ship Via"
              required
              validateStatus={!draft.shipVia.trim() ? "error" : ""}
              help={!draft.shipVia.trim() ? "Required" : undefined}
              style={{ margin: 0 }}
            >
              <Space direction="vertical">
                <Select
                  value={draft.shipVia || undefined}
                  size="large"
                  onChange={(val) => onDraftChange({ shipVia: val ?? "" })}
                  disabled={saving}
                  style={{ minWidth: 140 }}
                  options={shipViaOptions.map((v) => ({
                    label: v,
                    value: v,
                  }))}
                />
                {hasShipViaChanges(draft, version) && <PendingChangeNote />}
              </Space>
            </Form.Item>
          )} */}
          {hasValue(version.phone) && (
            <Descriptions.Item label="Phone">{version.phone}</Descriptions.Item>
          )}
          {hasValue(version.fax) && (
            <Descriptions.Item label="Fax">{version.fax}</Descriptions.Item>
          )}
          {hasValue(version.email) && (
            <Descriptions.Item label="Email">{version.email}</Descriptions.Item>
          )}
        </Descriptions>
      )}
    </>
  );
};
